import React from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Layout from './components/layout/layout';
import CustomerManagement from './pages/CustomerManagement';
import { CustomerTrash } from './pages/CustomerTrash';
import LicenseManagement from './pages/LicenseManagement';
import Analytics from './pages/Analytics.tsx';
import Login from './pages/Login';
import UserManagement from './pages/UserManagement';
import { UserTrash } from './pages/UserTrash';
import NewLicense from './pages/NewLicense';
import AuditLog from './pages/AuditLog';
import ReleaseManagement from './pages/ReleaseManagement';
import { ReleaseTrash } from './pages/ReleaseTrash';
import MachineDebug from './pages/MachineDebug';
import { ApiKeyManagement } from './pages/ApiKeyManagement';
import { ApiKeyTrash } from './pages/ApiKeyTrash';
import CustomerUsersManagement from './pages/CustomerUsersManagement';
import { CustomerUsersTrash } from './pages/CustomerUsersTrash';
import AccessDenied from './pages/AccessDenied';
import { AuthProvider } from './auth/authProvider';
import { PermissionRoute } from './components/routing/PermissionRoute';
import { DefaultRedirect } from './components/routing/DefaultRedirect';
import ToastManager from './components/Toast';
import { VersionInfo } from './components/common/VersionInfo';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <ToastManager>
            <VersionInfo />
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/access-denied" element={<AccessDenied />} />
              <Route path="/" element={<DefaultRedirect />} />
              <Route
                path="/*"
                element={
                  <Layout>
                    <Routes>
                      <Route
                        path="licenses"
                        element={
                          <PermissionRoute resource="licenses" action="read">
                            <LicenseManagement />
                          </PermissionRoute>
                        }
                      />
                      <Route
                        path="licenses/new"
                        element={
                          <PermissionRoute resource="licenses" action="create">
                            <NewLicense />
                          </PermissionRoute>
                        }
                      />
                      <Route
                        path="customers"
                        element={
                          <PermissionRoute resource="customers" action="read">
                            <CustomerManagement />
                          </PermissionRoute>
                        }
                      />
                      <Route
                        path="customers/trash"
                        element={
                          <PermissionRoute resource="customers" action="delete">
                            <CustomerTrash />
                          </PermissionRoute>
                        }
                      />
                      <Route
                        path="customer-users"
                        element={
                          <PermissionRoute resource="customer_users" action="read">
                            <CustomerUsersManagement />
                          </PermissionRoute>
                        }
                      />
                      <Route
                        path="customer-users/trash"
                        element={
                          <PermissionRoute resource="customer_users" action="permanent_delete">
                            <CustomerUsersTrash />
                          </PermissionRoute>
                        }
                      />
                      <Route
                        path="analytics"
                        element={
                          <PermissionRoute resource="analytics" action="read">
                            <Analytics />
                          </PermissionRoute>
                        }
                      />
                      <Route
                        path="users"
                        element={
                          <PermissionRoute resource="users" action="read">
                            <UserManagement />
                          </PermissionRoute>
                        }
                      />
                      <Route
                        path="users/trash"
                        element={
                          <PermissionRoute resource="users" action="delete">
                            <UserTrash />
                          </PermissionRoute>
                        }
                      />
                      <Route
                        path="audit"
                        element={
                          <PermissionRoute resource="audit" action="read">
                            <AuditLog />
                          </PermissionRoute>
                        }
                      />
                      <Route
                        path="releases"
                        element={
                          <PermissionRoute resource="updates" action="read">
                            <ReleaseManagement />
                          </PermissionRoute>
                        }
                      />
                      <Route
                        path="releases/trash"
                        element={
                          <PermissionRoute resource="updates" action="delete">
                            <ReleaseTrash />
                          </PermissionRoute>
                        }
                      />
                      <Route
                        path="machines/:machineId"
                        element={
                          <PermissionRoute resource="machines" action="read">
                            <MachineDebug />
                          </PermissionRoute>
                        }
                      />
                      <Route
                        path="api-keys"
                        element={
                          <PermissionRoute resource="api_keys" action="read">
                            <ApiKeyManagement />
                          </PermissionRoute>
                        }
                      />
                      <Route
                        path="api-keys/trash"
                        element={
                          <PermissionRoute resource="api_keys" action="delete">
                            <ApiKeyTrash />
                          </PermissionRoute>
                        }
                      />
                      <Route path="*" element={<DefaultRedirect />} />
                    </Routes>
                  </Layout>
                }
              />
            </Routes>
          </ToastManager>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
};

export default App;
